import { creatorbaseQueries, musicQueries } from '@round/api';
import { useMemo } from 'react';
import { useQueries } from '@tanstack/react-query';
import { isNumber } from 'utility/utility';
import { useSongsListInfinite } from '@round/creatorbase-helpers/api-hooks';
import type { SongsListInfiniteParams as Params } from '@round/creatorbase-helpers/api-hooks';
import moment from 'moment';
import uniq from 'lodash/uniq';

const musicOptions = musicQueries.song.options;
const timeSeriesOptions = creatorbaseQueries.timeSeries.options;

type Config = { enabled?: boolean };

export default function useSongsData(params: Params, config: Config = {}) {
    const today = moment().format('YYYY-MM-DD');
    const twoWeeksAgo = moment().subtract(14, 'days').format('YYYY-MM-DD');

    const { data, isLoading, isError, isFetchingNextPage, hasNextPage, fetchNextPage } = useSongsListInfinite(
        params,
        config
    );

    const songPageIndex = useMemo(() => {
        const map = new Map<number, number>();
        (data?.pages ?? []).forEach((page, i) => page.results.forEach((s) => map.set(s.id, i)));
        return map;
    }, [data?.pages]);

    const musicSongs = useQueries({
        queries: (data?.pages ?? []).map((page) => {
            const ids = uniq(page.results.map((s) => s.music_song_id).filter(isNumber));
            return {
                ...musicOptions.list({ id: ids.join(','), page_size: ids.length, page: 1 }),
                enabled: ids.length > 0,
            };
        }),
        combine: (queries) => {
            return {
                data: queries.flatMap((q) => q.data?.results ?? []),
                isLoadingFor: (songId: number) => queries[songPageIndex.get(songId) ?? -1]?.isLoading ?? false,
            };
        },
    });

    const timeSeries = useQueries({
        queries: (data?.pages ?? []).map((page) => ({
            ...timeSeriesOptions.list({
                type: 'song',
                ids: page.results.map((s) => s.id),
                fields: ['tiktok_daily_change', 'instagram_daily_change'],
                start_date: twoWeeksAgo,
                end_date: today,
            }),
            enabled: page.results.length > 0,
        })),
        combine: (queries) => {
            const tsBySong = new Map(
                queries.flatMap((q) => Object.entries(q.data ?? {}).map(([id, data]) => [Number(id), data] as const))
            );
            return {
                data: tsBySong,
                isLoadingFor: (songId: number) => queries[songPageIndex.get(songId) ?? -1]?.isLoading ?? false,
                isErrorFor: (songId: number) => queries[songPageIndex.get(songId) ?? -1]?.isError ?? false,
            };
        },
    });

    return {
        songs: (data?.pages ?? []).flatMap((p) => p.results),
        musicSongs,
        timeSeries,
        hasNextPage,
        isFetchingNextPage,
        fetchNextPage,
        isLoading,
        isError,
    };
}
